import { createTheme, Theme } from "@mui/material/styles";
import type { getColors } from "../../common/consts";
import type { ThemeMode } from "../../common/contexts/ThemeContext";

/**
 * HubSpot's look, for organizations whose CRM is HubSpot. Outreach orgs never
 * load any of this: every value here is applied only under
 * html[data-crm="hubspot"] or by a component that has already checked the
 * org's CRM (see components/CrmUi).
 *
 * Where the values come from
 * --------------------------
 * Live captures of app.hubspot.com (docs/hubspot-ui-reference/*.json: the
 * computed style of every node plus HubSpot's own CSS rules for each state),
 * taken 2026-10-04. HubSpot's current UI is not the old Canvas palette
 * (orange #ff7a59, navy, 3px corners) the dashboard used before:
 *
 *   Type       Lexend Deca; body and buttons at 300, 14/24 body, 12/14 small
 *              buttons, 14/18 at 600 large buttons, 16/20 at 600 card titles,
 *              20/24 at 500 dialog titles.
 *   Ink        #333333; secondary #666666; disabled #8A8A8A.
 *   Lines      #CCCCCC hairlines (cards, tables, popovers, tabs); #8A8A8A
 *              outlines on secondary buttons and search; #333333 on form
 *              fields and checkboxes.
 *   Page       #F5F5F5 behind white cards (record page scroll overhangs).
 *   Primary    #00494B, hover #007C7D, pressed #009B9C, white text; pills
 *              (border-radius 999999px).
 *   Secondary  white, #8A8A8A outline, #333 text; hover #EBFDFC, pressed #E6E6E6.
 *   Links      #006162 at 600, hover #00494B underlined.
 *   Focus      2px #016DE1 outline inset by 2px over a 2px white ring.
 *   Cards      16px corners, 1px #CCC, shadow 0 1px 8px rgba(20,20,20,.08).
 *   Popovers   16px corners, 1px #CCC, shadow 0 24px 48px rgba(20,20,20,.08).
 *   Modals     16px corners, shadow 0 16px 32px; overlay rgba(20,20,20,.46).
 *   Tooltips   #141414, 12/18 white, 6px corners, 10px 16px.
 *   Tables     40px rows, #CCC rules, #666 14px headers, #E6E6E6 row hover.
 *   Tabs       #CCC-framed grey (#F5F5F5) tabs, current one white at 500.
 *   Status     #11A389 (success), #E89E2A (warning), #AC0020 (invalid).
 *
 * Not captured, so built from the tokens above and marked UNVERIFIED where
 * they are drawn: the toggle (36x20 track, #CCC off / #00494B on, per the
 * task's brief), tags and alerts, the underline tab padding, the skeleton's
 * motion, and the whole dark theme (HubSpot has none).
 *
 * Our boring logo stays; none of HubSpot's marks are used, and the icons are
 * our own drawings in HubSpot's 32-grid line style (./icons.tsx).
 */

export const HUBSPOT_SANS = '"Lexend Deca", Helvetica, Arial, sans-serif';

export const HUBSPOT_FONT_URL = "https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap";

export const HS_TOKENS = {
  text: "#333333",
  textSecondary: "#666666",
  textTertiary: "#4d4d4d",
  textDisabled: "#8a8a8a",
  border: "#cccccc",
  borderStrong: "#8a8a8a",
  borderInput: "#333333",
  borderDisabled: "#e6e6e6",
  page: "#f5f5f5",
  surface: "#ffffff",
  fillHover: "#f0f0f0",
  fillPanel: "#ebebeb",
  fillPressed: "#e6e6e6",
  primary: "#00494b",
  primaryHover: "#007c7d",
  primaryActive: "#009b9c",
  secondaryHover: "#ebfdfc",
  link: "#006162",
  linkHover: "#00494b",
  focus: "#016de1",
  success: "#11a389",
  warning: "#e89e2a",
  danger: "#ac0020",
  tooltip: "#141414",
  overlay: "rgba(20, 20, 20, 0.46)",
  shadowCard: "0 1px 8px 0 rgba(20, 20, 20, 0.08)",
  shadowFloat: "0 24px 48px 0 rgba(20, 20, 20, 0.08)",
  shadowModal: "0 16px 32px 0 rgba(20, 20, 20, 0.08)",
  focusRing: "inset 0 0 0 2px #ffffff",
};

type Palette = ReturnType<typeof getColors>;

/* The dashboard's --color-* palette (common/consts.ts) with HubSpot's values;
   same keys, so ThemeContext swaps the whole set in one go. */
const HUBSPOT_LIGHT_COLORS: Palette = {
  backgroundWhite: "#ffffff",
  backgroundPrimary: "#f5f5f5",
  backgroundSecondary: "#f5f5f5",
  backgroundSecondaryCard: "#f5f5f5",
  backgroundBrand: "#f0f0f0",
  backgroundBrandLight: "#ebfdfc",
  backgroundBrandLightHover: "#f0f0f0",
  backgroundBrandDark: "#333333",
  backgroundBrandTab: "#f0f0f0",
  backgroundBrandTabHover: "#f0f0f0",
  backgroundBrandTable: "#ffffff",
  backgroundSwitch: "#cccccc",
  backgroundSwitchActive: "#00494b",
  backgroundSwitchHover: "#007c7d",
  borderBrandLight: "#00494b",
  borderDark: "#8a8a8a",
  borderPrimary: "#cccccc",
  borderSecondary: "#cccccc",
  borderTertiary: "#cccccc",
  foregroundBrand: "#006162",
  foregroundBrandLight: "#006162",
  foregroundBrandTable: "#333333",
  foregroundPrimary: "#333333",
  foregroundSecondary: "#666666",
  foregroundSecondaryLight: "#8a8a8a",
  foregroundSecondaryDark: "#666666",
  foregroundTertiary: "#333333",
  foregroundWhite: "#ffffff",
  chartBouncesPrevented: "#333333",
  chartEmailInvalid: "#ac0020",
  chartEmailValid: "#11a389",
  chartEmailQuestionable: "#e89e2a",
  chartEmailNoEmail: "#8a8a8a",
  chartEmailEnriched: "#006162",
  chartEmailProspectsChecked: "#016de1",
  chartAxisColor: "#666666",
  chartAxisLine: "#cccccc",
  outlineSwitchHover: "#f0f0f0",
  outlineInputHover: "#333333",
  outlineInputFocused: "#016de1",
  inputLabel: "#333333",
  integrationLoading: "#e89e2a",
  integrationConnected: "#11a389",
  integrationDisconnected: "#ac0020",
  dropdownIconColor: "#333333",
  inputOutline: "#333333",
  toggleInactive: "#cccccc",
  toggleInactiveHover: "#8a8a8a",
  toggleActive: "#00494b",
  toggleActiveHover: "#007c7d",
  saveButtonPrimary: "#00494b",
  saveButtonHover: "#007c7d",
  saveButtonDisabledBg: "#f5f5f5",
  saveButtonDisabledText: "#8a8a8a",
  saveButtonDisabledBorder: "#f5f5f5",
  saveButtonDisabledIcon: "#cccccc",
  tabActiveBg: "#ffffff",
  tabActiveText: "#333333",
  tabInactiveText: "#333333",
  tabHoverBg: "#f5f5f5",
  chipBorder: "#cccccc",
  statusDangerBg: "#fbf0f2",
  statusDangerText: "#333333",
  statusDangerIcon: "#ac0020",
  statusSuccessBg: "#eef8f6",
  statusSuccessText: "#333333",
  statusInfoBg: "#eef5fd",
  spinnerColor: "#8a8a8a",
};

/* UNVERIFIED: HubSpot's web app has no dark theme; this inverts the same
   neutral system so the dashboard's dark mode still reads as HubSpot. */
const HUBSPOT_DARK_COLORS: Palette = {
  ...HUBSPOT_LIGHT_COLORS,
  backgroundWhite: "#1f1f1f",
  backgroundPrimary: "#141414",
  backgroundSecondary: "#1a1a1a",
  backgroundSecondaryCard: "#1f1f1f",
  backgroundBrand: "#2b2b2b",
  backgroundBrandLight: "#0f3333",
  backgroundBrandLightHover: "#2b2b2b",
  backgroundBrandDark: "#2b2b2b",
  backgroundBrandTab: "#2b2b2b",
  backgroundBrandTabHover: "#2b2b2b",
  backgroundBrandTable: "#1f1f1f",
  backgroundSwitch: "#5c5c5c",
  backgroundSwitchActive: "#007c7d",
  borderBrandLight: "#5cc6c2",
  borderDark: "#8a8a8a",
  borderPrimary: "#3d3d3d",
  borderSecondary: "#3d3d3d",
  borderTertiary: "#3d3d3d",
  foregroundBrand: "#5cc6c2",
  foregroundBrandLight: "#5cc6c2",
  foregroundBrandTable: "#f0f0f0",
  foregroundPrimary: "#f0f0f0",
  foregroundSecondary: "#b3b3b3",
  foregroundSecondaryLight: "#8a8a8a",
  foregroundSecondaryDark: "#b3b3b3",
  foregroundTertiary: "#f0f0f0",
  foregroundWhite: "#f0f0f0",
  chartBouncesPrevented: "#f0f0f0",
  chartAxisColor: "#b3b3b3",
  chartAxisLine: "#3d3d3d",
  outlineSwitchHover: "#2b2b2b",
  outlineInputHover: "#b3b3b3",
  inputLabel: "#f0f0f0",
  dropdownIconColor: "#b3b3b3",
  inputOutline: "#b3b3b3",
  toggleInactive: "#5c5c5c",
  toggleInactiveHover: "#8a8a8a",
  toggleActive: "#007c7d",
  saveButtonDisabledBg: "#262626",
  saveButtonDisabledText: "#8a8a8a",
  saveButtonDisabledBorder: "#262626",
  saveButtonDisabledIcon: "#5c5c5c",
  tabActiveBg: "#1f1f1f",
  tabActiveText: "#f0f0f0",
  tabInactiveText: "#f0f0f0",
  tabHoverBg: "#262626",
  chipBorder: "#3d3d3d",
  statusDangerBg: "#2e1418",
  statusDangerText: "#f0f0f0",
  statusSuccessBg: "#12291f",
  statusSuccessText: "#f0f0f0",
  statusInfoBg: "#14233a",
  spinnerColor: "#8a8a8a",
};

export const getHubSpotColors = (mode: ThemeMode = "light"): Palette => (mode === "dark" ? HUBSPOT_DARK_COLORS : HUBSPOT_LIGHT_COLORS);

/**
 * MUI theme for HubSpot orgs: Lexend Deca at 300, HubSpot's teal as MUI's
 * primary (so spinners and focused fields pick it up), 6px small corners,
 * and pill buttons. The rest of each MUI part's look (menus, dialogs,
 * tooltips, tables) is in styles/hubspot.css and the kit's stylesheet.
 * Outreach orgs render without a theme provider at all, exactly as before.
 */
export const createHubSpotMuiTheme = (mode: ThemeMode): Theme => {
  const dark = mode === "dark";
  const c = getHubSpotColors(mode);

  return createTheme({
    palette: {
      mode,
      primary: { main: HS_TOKENS.primary, dark: HS_TOKENS.primary, light: HS_TOKENS.primaryHover, contrastText: "#ffffff" },
      secondary: { main: HS_TOKENS.link, dark: HS_TOKENS.linkHover, light: HS_TOKENS.primaryHover, contrastText: "#ffffff" },
      error: { main: HS_TOKENS.danger },
      warning: { main: HS_TOKENS.warning },
      success: { main: HS_TOKENS.success },
      info: { main: HS_TOKENS.focus },
      text: { primary: c.foregroundPrimary, secondary: c.foregroundSecondary, disabled: HS_TOKENS.textDisabled },
      divider: c.borderPrimary,
      background: { default: c.backgroundPrimary, paper: c.backgroundWhite },
      action: { hover: dark ? "#2b2b2b" : HS_TOKENS.fillHover, selected: dark ? "#2b2b2b" : HS_TOKENS.fillHover },
    },
    shape: { borderRadius: 6 },
    typography: {
      fontFamily: HUBSPOT_SANS,
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 300,
      fontWeightMedium: 500,
      fontWeightBold: 600,
      button: { textTransform: "none", fontWeight: 300 },
    },
    transitions: {
      easing: { easeOut: "ease-out", easeInOut: "ease-out", easeIn: "ease-in", sharp: "ease-out" },
      duration: { shortest: 150, shorter: 150, short: 150, standard: 150, complex: 250, enteringScreen: 250, leavingScreen: 150 },
    },
    components: {
      MuiButtonBase: { defaultProps: { disableRipple: true } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 999999, fontFamily: HUBSPOT_SANS, fontWeight: 300, boxShadow: "none" },
        },
      },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: 16 } } },
      MuiTooltip: { defaultProps: { arrow: true } },
      MuiMenu: { defaultProps: { elevation: 0 } },
      MuiPopover: { defaultProps: { elevation: 0 } },
      MuiDialog: { defaultProps: { PaperProps: { elevation: 0 } } },
      MuiTabs: { styleOverrides: { indicator: { height: 5, borderRadius: 24, bottom: -2.5, backgroundColor: c.foregroundPrimary } } },
    },
  });
};

/**
 * For a component's own styled card: under html[data-crm="hubspot"] it takes
 * HubSpot's card chrome (16px corners, #CCC hairline, the soft card shadow).
 * Interpolate it into the styled template; Outreach orgs never match.
 */
export const HUBSPOT_TILE = `
  html[data-crm="hubspot"] & {
    box-shadow: var(--hs-shadow-card, 0 1px 8px 0 rgba(20, 20, 20, 0.08));
    border: 1px solid var(--hs-border, #cccccc);
    border-radius: 16px;
  }
`;
